import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Users, Star } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import emailjs from '@emailjs/browser';
import ReCAPTCHA from 'react-google-recaptcha';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};


const companions = [
  {
    name: 'Sarah Mitchell',
    role: 'Senior Companion',
    image: 'https://images.unsplash.com/photo-1648414749870-282b53b1548f?w=500&auto=format&fit=crop&q=80',
    bio: 'Sarah brings over 8 years of experience in community support. Known for her warm, patient nature and genuine love for meaningful conversations, she has a special talent for making everyone feel at ease.',
    qualities: ['Patient', 'Empathetic', 'Great Listener']
  },
  {
    name: 'James Thompson',
    role: 'Companion',
    image: 'https://images.pexels.com/photos/5698208/pexels-photo-5698208.jpeg?w=500&auto=compress&cs=tinysrgb',
    bio: 'James is a friendly and reliable companion who genuinely enjoys helping others stay active and engaged with their communities. His positive outlook and easy-going personality make every visit enjoyable.',
    qualities: ['Reliable', 'Energetic', 'Community-minded']
  },
  {
    name: 'Emma Clarke',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1605888357467-9caf88c89f67?w=500&auto=format&fit=crop&q=80',
    bio: 'Emma is compassionate and attentive, creating a comfortable environment where everyone feels valued and heard. She has a natural ability to connect with people from all walks of life.',
    qualities: ['Compassionate', 'Attentive', 'Thoughtful']
  },
  {
    name: 'Michael Roberts',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1710952615354-3e5e9f234fc7?w=500&auto=format&fit=crop&q=80',
    bio: 'Michael brings energy and warmth to every visit he makes. Dedicated and thoughtful, he takes pride in building genuine relationships with everyone he supports.',
    qualities: ['Dedicated', 'Warm', 'Professional']
  },
  {
    name: 'Helen Williams',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1694857561361-b66b3d1a6cc8?w=500&auto=format&fit=crop&q=80',
    bio: 'Helen has a natural gift for putting people at ease. Her calm demeanour and genuine interest in others makes her an exceptional companion for those seeking meaningful connection.',
    qualities: ['Calm', 'Genuine', 'Supportive']
  },
  {
    name: 'David Roberts',
    role: 'Companion',
    image: 'https://images.unsplash.com/photo-1620475887186-6e217289a5d4?w=500&auto=format&fit=crop&q=80',
    bio: 'David combines professionalism with a personable approach. He enjoys outdoor activities and helping individuals maintain their independence through community engagement.',
    qualities: ['Professional', 'Active', 'Encouraging']
  },
];

const values = [
  { icon: Heart, title: 'Compassion First', description: 'Every companion is selected for their genuine warmth and care.' },
  { icon: Users, title: 'DBS Checked', description: 'All companions undergo thorough background checks for your peace of mind.' },
  { icon: Star, title: 'Continuous Training', description: 'Our team receives ongoing development to maintain excellence.' },
];

export const CompanionsPage = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.name.trim()) return alert('Full name is required');
    if (!formData.email.trim()) return alert('Email is required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return alert('Enter a valid email');
    if (!formData.phone.trim()) return alert('Phone number is required');
    if (!formData.location.trim()) return alert('Location is required');
    if (!captcha) {  alert("Please verify you are not a robot");  return;}

    if (!captcha) {
  alert("Please verify you are not a robot");
  return;
}

setSending(true);

emailjs.send(
  'service_8diavld',
  'template_gc737zh',
  {
    name: formData.name,
    email: formData.email,
    phone: formData.phone,
    support_for: 'Staff Application',
    support_type: formData.location,
    contact_method: 'Application Form Request',
    message: formData.message,
    'g-recaptcha-response': captcha
  },
  '5LCZWAm1bEoxz1KzZ'
)
      .then(() => {
        alert('Application request sent. HR will be in touch.');
        setOpen(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          location: '',
          message: ''
        });
      })
      .catch((error) => {
        console.error('EmailJS error:', error);
        alert('Failed to send. Try again.');
      })
      .finally(() => {
        setSending(false);
      });
  };

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-white" data-testid="companions-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#3A7BD5] mb-4 block">
              Our Team
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1E4F8A] leading-tight mb-6">
              Meet Your Companions
            </h1>
            <p className="text-lg text-[#4A5568] leading-relaxed">
              Our carefully selected companions bring warmth, reliability, and genuine care to every
              interaction. Each team member is chosen for their compassionate nature and commitment
              to enhancing the lives of those they support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values Strip */}
      <section className="py-12 bg-[#1E4F8A]" data-testid="companions-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <value.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">{value.title}</h3>
                  <p className="text-sm text-white/70">{value.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Companions Grid */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="companions-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {companions.map((companion, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full bg-white border-[#E2E8F0] overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={companion.image}
                      alt={companion.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <h3 className="font-serif text-xl text-[#1E4F8A] mb-1">{companion.name}</h3>
                    <p className="text-sm text-[#3A7BD5] font-medium mb-4">{companion.role}</p>
                    <p className="text-[#4A5568] text-sm leading-relaxed mb-4">{companion.bio}</p>
                    <div className="flex flex-wrap gap-2">
                      {companion.qualities.map((quality, qIndex) => (
                        <span
                          key={qIndex}
                          className="px-3 py-1 bg-[#F7F9FC] text-[#4A5568] text-xs font-medium rounded-full"
                        >
                          {quality}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Companion Matching */}
      <section className="py-20 md:py-28 bg-white" data-testid="matching-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <Users className="w-12 h-12 text-[#3A7BD5] mx-auto mb-6" />
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
              Thoughtful Companion Matching
            </h2>
            <p className="text-lg text-[#4A5568] mb-4">
              We take great care in matching companions with those they support. We consider personality,
              interests, and preferences to ensure a comfortable and enjoyable experience for everyone involved.
            </p>
            <p className="text-[#4A5568] mb-8">
              During your initial consultation, we&apos;ll discuss your preferences and requirements to find
              the perfect companion match for you or your loved one.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center bg-[#1E4F8A] text-white px-8 py-4 rounded-full font-medium hover:bg-[#163A66] transition-colors"
              data-testid="matching-cta"
            >
              Book a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#F7F9FC]" data-testid="companions-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-3xl md:text-4xl text-[#1E4F8A] mb-6">
              Join Our Team of Companions
            </h2>
            <p className="text-lg text-[#4A5568] mb-8">
              Are you a compassionate individual looking to make a real difference in people&apos;s lives?
              We&apos;re always interested in hearing from caring people who share our values.
            </p>
            <button
              onClick={() => setOpen(true)}
              className="inline-flex items-center border-2 border-[#1E4F8A] text-[#1E4F8A] px-8 py-4 rounded-full font-medium hover:bg-[#1E4F8A] hover:text-white transition-colors"
              data-testid="join-team-cta"
            >
              Apply to Join
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Application Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-lg relative shadow-xl max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-[#1E4F8A] text-xl"
              aria-label="Close"
            >
              ✕
            </button>

            <h3 className="text-2xl font-serif text-[#1E4F8A] mb-2 text-center">
              Join AERW Lifestyle
            </h3>
            <p className="text-[#4A5568] text-center mb-6">
              Complete this short form and our HR team will send you a full application form.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                placeholder="Full Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-[#E2E8F0] p-3 rounded-lg focus:outline-none focus:border-[#3A7BD5]"
              />

              <input
                placeholder="Email Address"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border border-[#E2E8F0] p-3 rounded-lg focus:outline-none focus:border-[#3A7BD5]"
              />

              <input
                placeholder="Phone Number"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full border border-[#E2E8F0] p-3 rounded-lg focus:outline-none focus:border-[#3A7BD5]"
              />

              <input
                placeholder="Your Location (City)"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full border border-[#E2E8F0] p-3 rounded-lg focus:outline-none focus:border-[#3A7BD5]"
              />

              <textarea
                placeholder="Tell us briefly about yourself"
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full border border-[#E2E8F0] p-3 rounded-lg focus:outline-none focus:border-[#3A7BD5] resize-none"
              />
              <ReCAPTCHA
                sitekey="6LcjHZ0sAAAAAFSs4jy7TuNLd7a8aCPPi6zP5K2y"
                onChange={(value) => setCaptcha(value)}
             />
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-[#1E4F8A] text-white py-3 rounded-full font-medium hover:bg-[#163A66] transition-colors disabled:opacity-70"
              >
                {sending ? 'Sending...' : 'Request Application Form'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanionsPage;